import type { ArrayValues } from 'type-fest'
import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import {
  DEFAULT_CLIPSKIP_VAL_FOR_NON_SDV1,
  DEFAULT_CLIPSKIP_VAL_FOR_SDV1,
} from 'src/components/GenerationTask/constants'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import type { GenerationModelExtra } from 'src/services/helpers/schema/modelExtra'
import type { GenerationModelVersionExtra } from 'src/services/helpers/schema/modelVersionExtra'
import {
  canModelUsePresetQualityTags,
  canModelUseStyleKey,
  canModelUseStyleMix,
  DEFAULT_STYLE_MIX_WEIGHT,
  getModelQualityTags,
  getRandomStyleMixRaw,
  isModelCompatibleWithVae,
  isMMDIT26AModel,
  modelSupportsUpscale,
  hasModelEnabledInferenceConfig,
  hasModelEnabledLightning,
  hasModelEnabledRescaleCfg,
  hasModelEnabledShiftValue,
  getModelDefaultSizeDivisor,
  getDefaultSizeByModelType,
  isSDV1Model,
  LIGHTNING_MODE_DEFAULT_PARAMS,
  retrieveQualityTagFromLocalStorage,
  isChatModel,
} from '../model'
import { emptyHiResParams } from '../task'

function getDefaultShiftValueParams(
  versionExtra?: GenerationModelVersionExtra,
) {
  if (hasModelEnabledShiftValue(versionExtra)) {
    const defaultValue = versionExtra?.defaults?.shift as number | undefined

    return {
      modelSampling: {
        inputs: {
          shift: defaultValue,
        },
      },
    }
  }
}
function getDefaultRescaleCfgParams(
  versionExtra?: GenerationModelVersionExtra,
) {
  if (hasModelEnabledRescaleCfg(versionExtra)) {
    const defaultValue = versionExtra?.defaults?.rescaleCfg?.enabledValue
    const defaultEnabled = versionExtra?.defaults?.rescaleCfg?.enable

    return {
      rescaleCFG: defaultEnabled ? defaultValue : undefined,
    }
  }
}

/**
 * Align size to the nearest multiple of divisor
 * Returns empty object if no alignment needed or size is invalid
 */
function alignSizeToDivisor(
  size: { width?: number; height?: number },
  divisor: { width: number; height: number },
): { width?: number; height?: number } {
  if (size.width == null || size.height == null) return {}

  const alignedWidth = Math.floor(size.width / divisor.width) * divisor.width
  const alignedHeight =
    Math.floor(size.height / divisor.height) * divisor.height

  // Only return if alignment changed something
  if (alignedWidth !== size.width || alignedHeight !== size.height) {
    return { width: alignedWidth, height: alignedHeight }
  }
  return {}
}

/** Max allowed longest edge for manual input (all models cap at 1400). */
function getModelMaxEdge(_modelType?: GenerationModelType): number {
  return 1400
}

/**
 * Get size divisor based on model type
 * Reuses logic from getModelSizeDivisor but works with GenerationModelVersionExtra
 */
function getModelDivisor(
  modelType?: GenerationModelType,
  versionExtra?: GenerationModelVersionExtra,
): { width: number; height: number } {
  const widthStep = versionExtra?.restrictions?.width?.step
  const heightStep = versionExtra?.restrictions?.height?.step
  const defaultStep = getModelDefaultSizeDivisor(modelType)
  return {
    width: widthStep || defaultStep,
    height: heightStep || defaultStep,
  }
}
function getModelDefaultStyleMixSeed(
  versionExtra?: GenerationModelVersionExtra,
) {
  if (typeof versionExtra?.styleMixSeed === 'number') {
    return versionExtra.styleMixSeed
  }
}

const fieldsToChangeForModelVersionExtra = [
  'negativePrompts',
  'samplingSteps',
  'samplingMethod',
  'cfgScale',
] as const
type ModelExtraFields = ArrayValues<typeof fieldsToChangeForModelVersionExtra>
/**
 * Future default params should all be declared here
 * (Version.extra.defaults)
 */
const fieldsToChangeForModelVersionExtraDefaults = [
  'batchSize',
  'upscaleDenoisingStrength',
  'upscaleDenoisingSteps',
  'upscaleSampler',
] as const
type ModelVersionExtraDefaultFields = ArrayValues<
  typeof fieldsToChangeForModelVersionExtraDefaults
>

/**
 * For model import / model switching
 * @param versionExtra
 * @param options
 * @returns
 */
export function getDefaultParamsFromModelVersionExtra(
  valueExtras: {
    versionExtra?: GenerationModelVersionExtra
    /** Model ID is needed for retrieving preset quality tags; NOT version ID */
    modelId?: string
    modelType?: GenerationModelType
  },
  options?: {
    enableLightningMode?: boolean
  },
): TaskSubmitFormValues {
  const { versionExtra = {}, modelId } = valueExtras
  const { defaults: versionExtraDefaults = {} } = versionExtra ?? {}
  const vals = Object.fromEntries([
    ...fieldsToChangeForModelVersionExtra.flatMap(field => {
      const defaultVal =
        field in versionExtra ? versionExtra?.[field] : undefined

      if (defaultVal != null) {
        return [[field, defaultVal]]
      }
      return []
    }),

    ...fieldsToChangeForModelVersionExtraDefaults.flatMap(field => {
      const defaultVal =
        field in versionExtraDefaults ? versionExtraDefaults[field] : undefined

      if (defaultVal != null) {
        return [[field, defaultVal]]
      }
      return []
    }),
  ]) as Partial<
    Record<ModelExtraFields, any> & Record<ModelVersionExtraDefaultFields, any>
  >

  /**
   * If is importing task and task didn't gen with styleMix
   * Don't provide versionExtra to skip styleMix params
   */
  const shouldEnableStyleMix = canModelUseStyleMix(versionExtra)
  const defaultStyleMixSeed = getModelDefaultStyleMixSeed(versionExtra)

  const styleMixParams = shouldEnableStyleMix
    ? {
        weight: DEFAULT_STYLE_MIX_WEIGHT,
        seed: defaultStyleMixSeed ?? getRandomStyleMixRaw(),
        enabled: true,
      }
    : {
        enabled: false,
      }

  /**
   * If is importing task and task didn't gen with qualityTag
   * Don't provide versionExtra to skip qualityTag params
   */
  const shouldEnableQualityTag = canModelUsePresetQualityTags(versionExtra)
  /**
   * Quality tags come from 3 sources
   *
   * 1. Task Imports
   * 2. Saved Tags (in LocalStorage)
   * 3. First Tag in model data
   */
  const qualityTagParams = shouldEnableQualityTag
    ? {
        ...getModelQualityTags(versionExtra)?.[0],
        // Saved Tags in localStorage
        ...retrieveQualityTagFromLocalStorage(modelId),
      }
    : undefined

  const shouldEnableLightning =
    options?.enableLightningMode && hasModelEnabledLightning(versionExtra)
  const lightningModeParams = shouldEnableLightning
    ? LIGHTNING_MODE_DEFAULT_PARAMS
    : { lightning: false }

  /* Disable hires by version extra */
  const hiresConfigParams = !modelSupportsUpscale(versionExtra)
    ? {
        upscale: undefined,
        upscaleDenoisingSteps: undefined,
        upscaleSampler: undefined,
      }
    : {}

  const inferenceConfigParmas = hasModelEnabledInferenceConfig(versionExtra)
    ? {
        // Skip rescaleCfg field unless compatible
        ...getDefaultRescaleCfgParams(versionExtra),
        ...getDefaultShiftValueParams(versionExtra),
      }
    : undefined

  const defaultStyleKeyId = versionExtraDefaults.defaultStyleKeyId as
    | string
    | undefined
  const defaultModelStyle =
    canModelUseStyleKey(versionExtra) && defaultStyleKeyId
      ? { type: 'key' as const, keyId: defaultStyleKeyId }
      : undefined

  return {
    ...vals,
    styleMix: styleMixParams,
    qualityTag: qualityTagParams,
    inferenceConfig: inferenceConfigParmas,
    modelStyle: defaultModelStyle,
    ...lightningModeParams,
    ...hiresConfigParams,
  }
}

export function getParamsForModelChange(
  targets: {
    modelId?: string
    modelExtra?: GenerationModelExtra
    versionId: string
    versionExtra?: GenerationModelVersionExtra
    modelType?: GenerationModelType
    currentModelType?: GenerationModelType
  },
  options: {
    hiresEnabled?: boolean
    currentWidth?: number
    currentHeight?: number
  } & Parameters<typeof getDefaultParamsFromModelVersionExtra>[1],
) {
  const { versionId, modelId, versionExtra, modelType, currentModelType } =
    targets
  const {
    hiresEnabled,
    currentWidth,
    currentHeight,
    ...getModelDefaultParams
  } = options

  // Both types exist and not equal
  const modelTypeChanged =
    modelType != null &&
    currentModelType != null &&
    modelType !== currentModelType
  const shouldResetHires = modelTypeChanged && hiresEnabled

  // Align current size to new model's divisor requirements
  const divisor = getModelDivisor(modelType, versionExtra)
  const alignedSize = alignSizeToDivisor(
    { width: currentWidth, height: currentHeight },
    divisor,
  )

  // Clamp size if it exceeds the new model's max edge
  const effectiveWidth = alignedSize.width ?? currentWidth
  const effectiveHeight = alignedSize.height ?? currentHeight
  const maxEdge = getModelMaxEdge(modelType)
  const shouldResetSize =
    effectiveWidth != null &&
    effectiveHeight != null &&
    (effectiveWidth > maxEdge || effectiveHeight > maxEdge)
  const sizeOverride = shouldResetSize
    ? getDefaultSizeByModelType(modelType)
    : {}

  const modelDefaultParams = getDefaultParamsFromModelVersionExtra(
    {
      versionExtra,
      modelId,
      modelType,
    },
    getModelDefaultParams,
  )

  return {
    values: {
      modelId: versionId,

      ...modelDefaultParams,

      // Align size to model's divisor requirements (e.g., 8 for SD, 16 for DiT)
      ...alignedSize,
      // Reset to default if size exceeds the new model's max edge
      ...sizeOverride,

      clipSkip: isSDV1Model(modelType)
        ? DEFAULT_CLIPSKIP_VAL_FOR_SDV1
        : DEFAULT_CLIPSKIP_VAL_FOR_NON_SDV1,
      // Clear VAE whenever switching TO a VAE-compatible model type.
      // VAE pools are type-specific (SDv1 ≠ SDXL), and a stored VAE ID from
      // a previous VAE-compatible type (e.g. SDv1 VAE lingering after
      // SDv1 → DiT → SDXL) would be invalid for the new type and would cause
      // an API error. Clearing on any VAE-compatible destination is the safest
      // approach since we cannot validate pool membership without live data.
      ...(modelTypeChanged && isModelCompatibleWithVae(modelType)
        ? { vaeModelId: undefined }
        : {}),
      // MultiImageReferenceSection is only rendered for chat models. Switching
      // away from a chat/edit model to any other type hides the section and its
      // remove buttons, leaving multiImageReference stuck in form state. Clear
      // it on switch so the dangling refs don't silently carry over.
      ...(!isChatModel(modelType)
        ? {
            multiImageReference: undefined,
          }
        : {}),
      // ReferenceImageControl is hidden for MMDIT26A (Tsubaki.2), so any
      // baseImage carried over from a previous edit/i2i task would be stuck
      // with no way to remove it. Wipe on switch.
      ...(isMMDIT26AModel(modelType)
        ? {
            baseImage: undefined,
            previewImage: undefined,
            inpaintMaskImage: undefined,
          }
        : {}),
      inferenceProfile: undefined,
      /**
       * If model type changes, reset hires params
       */
      ...(shouldResetHires ? emptyHiResParams : {}),
    } satisfies Partial<TaskSubmitFormValues>,
    hasResetHires: shouldResetHires,
    hasResetSize: shouldResetSize,
  }
}
